"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

// TabsList — chip layout matching FE bk `.tab-btn` group. Each
// trigger is a pill that switches background to brand brown when
// active. No surrounding bg-muted track — list is just a flex
// container with a small gap between chips.
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    // `scroll-hint-x`: bóng mờ ở mép cho biết hàng thẻ còn tiếp bên phải —
    // xem chú thích ở globals.css. Chỉ có tác dụng dưới 768px, nơi hàng thẻ
    // cuộn ngang; từ md trở lên hàng thẻ tự xuống dòng nên lớp nền tắt.
    className={cn("scroll-hint-x inline-flex items-center gap-2 max-w-full overflow-x-auto scrollbar-hide snap-x snap-mandatory md:flex-wrap md:overflow-x-visible", className)}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

// TabsTrigger — chip styled to match `.tab-btn`:
//   inactive  → border, surface bg, muted text;  hover → surface-2 bg
//   active    → brand bg, white text
// Wrap children in a label + optional <TabCount> to get the bk look
// with the count chip flipping color on active state.
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "snap-start shrink-0 group/tab inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-border bg-surface px-4 py-2 text-[13px] font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-[var(--color-surface-2)] hover:text-foreground data-[state=active]:border-[var(--color-brand)] data-[state=active]:bg-[var(--color-brand)] data-[state=active]:text-white",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

// TabCount — count chip next to a tab label. Reads the parent
// trigger's data-state via `group-data-[state=active]/tab:` so it
// inverts color on active without consumer needing to manage state.
function TabCount({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        "ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold",
        "bg-[var(--color-surface-2)] text-muted-foreground",
        "group-data-[state=active]/tab:bg-black/20 group-data-[state=active]/tab:text-white",
        className
      )}
    >
      {children}
    </span>
  )
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, TabCount }
